import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { firebaseAuth } from './config/firebase.config';
import { FirebaseAuthProvider, useAuth } from './services/auth';
import { AuthRoutes } from './services/navigation/AuthRoutes';
import { MainRoutes } from './services/navigation/MainRoutes';
import { LoadingPage } from './pages/LoadingPage';
import { NoSleepView } from './components/no_sleep/NoSleep';
import { reloadScreen } from './unboared/config/screenSocketAudit';

/**
 * Écran de dernier recours. Volontairement sans `useTranslation` ni aucun hook :
 * si le crash vient de React ou d'i18next, un hook ici ferait planter le fallback
 * lui-même — et une erreur levée dans le fallback d'un ErrorBoundary remonte, donc
 * page blanche. On lit la langue déjà posée sur `<html>` par main.tsx.
 */
const FALLBACK_COPY: Record<string, { title: string; body: string; action: string }> = {
    en: {
        title: 'Oops, something went wrong',
        body: 'An unexpected error occurred. Please reload the page.',
        action: 'Reload',
    },
    fr: {
        title: 'Oups, un problème est survenu',
        body: 'Une erreur inattendue s’est produite. Recharge la page.',
        action: 'Recharger',
    },
    es: {
        title: 'Vaya, algo ha salido mal',
        body: 'Se ha producido un error inesperado. Vuelve a cargar la página.',
        action: 'Recargar',
    },
    it: {
        title: 'Ops, qualcosa è andato storto',
        body: 'Si è verificato un errore imprevisto. Ricarica la pagina.',
        action: 'Ricarica',
    },
    pt: {
        title: 'Ups, algo correu mal',
        body: 'Ocorreu um erro inesperado. Recarregue a página.',
        action: 'Recarregar',
    },
    de: {
        title: 'Ups, da ist etwas schiefgelaufen',
        body: 'Ein unerwarteter Fehler ist aufgetreten. Bitte lade die Seite neu.',
        action: 'Neu laden',
    },
    zh: {
        title: '抱歉，出错了',
        body: '发生了意外错误，请重新加载页面。',
        action: '重新加载',
    },
};

function ErrorFallback() {
    const lang = (document.documentElement.lang || 'en').split('-')[0];
    const copy = FALLBACK_COPY[lang] ?? FALLBACK_COPY.en;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                gap: '1rem',
                padding: '1.5rem',
                textAlign: 'center',
                fontFamily: 'sans-serif',
                color: 'white',
                background: '#1a1a2e',
            }}
        >
            <h1>{copy.title}</h1>
            <p>{copy.body}</p>
            <button
                type="button"
                onClick={() => reloadScreen('page-erreur')}
                style={{
                    padding: '0.75rem 2rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#6c63ff',
                    color: 'white',
                    cursor: 'pointer',
                }}
            >
                {copy.action}
            </button>
        </div>
    );
}

function AppStack() {
    const { t } = useTranslation();
    const { initialLoading, auth } = useAuth();

    if (initialLoading) {
        return <LoadingPage>{t('global.loadingAccount')}</LoadingPage>;
    }

    return auth ? <MainRoutes /> : <AuthRoutes />;
}

export default function App() {
    return (
        <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
            <FirebaseAuthProvider firebaseAuth={firebaseAuth}>
                <AppStack />
                <NoSleepView />
            </FirebaseAuthProvider>
        </Sentry.ErrorBoundary>
    );
}
